/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-09
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { selectSuccessNotifications } from '../../features/notifications';
import { useSuccessNotification } from '../../hooks/useSuccessNotification';
import SuccessAlert, { SuccessType } from './SuccessAlert';
import type { RootState } from '../../store';

interface SuccessAlertContainerProps {
  /**
   * Filter notifications by success type(s)
   * If provided, only notifications matching these types will be displayed
   */
  filterByType?: SuccessType | SuccessType[];
  /**
   * Context identifier for targeted notifications
   * If provided, only notifications with matching context will be displayed
   */
  context?: string;
  /**
   * Whether to show all notifications (default) or only contextual ones
   */
  showAll?: boolean;
}

/**
 * Container component that manages success notifications from Redux store
 * and renders SuccessAlert components for each notification.
 *
 * This component automatically displays all active success notifications
 * and handles their dismissal through the notification system.
 *
 * @example
 * // Place in App component to show notifications globally
 * <SuccessAlertContainer />
 *
 * // Show only password-related notifications in a form
 * <SuccessAlertContainer filterByType="password" context="password-reset-form" />
 *
 * // Show multiple types of notifications
 * <SuccessAlertContainer filterByType={["password", "general"]} />
 *
 * @param {SuccessAlertContainerProps} props - Component props
 * @returns {React.ReactElement | null} The success alert container component
 */
const SuccessAlertContainer: React.FC<SuccessAlertContainerProps> = ({
  filterByType,
  context: _context,
  showAll: _showAll = true,
}) => {
  const allNotifications = useSelector((state: RootState) =>
    selectSuccessNotifications(state)
  );
  const { hideSuccess } = useSuccessNotification();

  // Filter notifications based on props
  const notifications = React.useMemo(() => {
    let filtered = allNotifications;

    // If filterByType is provided, filter by success type
    if (filterByType) {
      const types = Array.isArray(filterByType) ? filterByType : [filterByType];
      filtered = filtered.filter(notification =>
        types.includes(notification.successType)
      );
    }

    // For contextual containers, we can add context-based filtering in the future
    // For now, we'll use the type filtering as the primary mechanism

    return filtered;
  }, [allNotifications, filterByType]);

  // Don't render anything if there are no notifications
  if (notifications.length === 0) {
    return null;
  }

  return (
    <>
      {notifications.map(notification => (
        <SuccessAlert
          key={notification.id}
          message={notification.message}
          successType={notification.successType}
          showAction={notification.showAction}
          actionLabel={notification.actionLabel}
          autoHide={notification.autoHide}
          hideDelay={notification.hideDelay}
          onDismiss={() => hideSuccess(notification.id)}
        />
      ))}
    </>
  );
};

export default SuccessAlertContainer;
